import { useState, useEffect, Activity, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectAllAttendeesLoading,
  selectAllAttendeesPage,
  selectAllAttendeesPageSize,
  selectAllAttendeesSort,
  selectAllAttendeesSortDirection,
  selectAllAttendeesTotal,
  selectAllAttendeesUsers,
} from '@/store/slices/audiences/selectors';
import {
  setAllAttendeesFields,
  setAllAttendeesPage,
  setAllAttendeesPageSize,
  setAllAttendeesSort,
  resetAllAttendees,
} from '@/store/slices/audiences';
import { AttendeeUserDefault, type UserStatus } from '@/types/all-attendees.types';
import type { ImportedProfile } from '@/types/passcode-list.types';
import Table from '@layouts/layout-table/layout-table';
import TableBody from '@layouts/layout-table/layout-table-body';
import TableHead from '@layouts/layout-table/layout-table-head';
import TableRow from '@layouts/layout-table/layout-table-row';
import OptionalComponent from '@ui/optional-component/optional-component';
import Paging from '@ui/paging/paging';
import SortButton from '@ui/buttons/sort-button/sort-button';
import { SortDirection } from '@ui/buttons/sort-button/types';
import Checkbox from '@ui/checkbox/checkbox';
import { useLoadAllAttendeesList } from '../../hooks/listsv2/use-load-all-attendees-list';
import {
  COLUMN_DEFINITIONS,
  DEFAULT_COLUMN_IDS_WEBINAR,
  buildAttendeeFields,
  regFieldId,
  REG_FIELD_ID_PREFIX,
  DEFAULT_COLUMN_IDS_TOWNHALL,
} from './profile-column-definitions';
import ProfileListActions from './profile-list-actions';
import ProfileListRow from './profile-list-row';
import ProfileColumnsSelect from './profile-columns-select';
import AddUserToList from './add-user-to-list';
import AllProfilesBulkActions from './all-profiles-bulk-actions';
import NoProfiles from './no-profiles';
import { selectWorkingProjectRegistrationQuestions, selectWorkingProjectUuid } from '@/store/slices/project/selectors';
import { selectActiveChannel, selectUserChannel } from '@/store/slices/user/selectors';
import { isWebinarChannel } from '@/utils/project-types/project-type-guards';
import AddUsersToList from '@/connection/audiences/add-users-to-list';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';
import Container from '@ui/container/container';
import CustomSelect from '@ui/select/custom-select/custom-select';
import Text from '@ui/text/text/text';
import usePopover from '@ui/popover/use-popover';
import { ICONS } from '@ui/icons/types';
import { RegFieldsEnum } from '@/types/registration.types';
import { useCallbackRef } from '@/utils/use-callback-ref';

const PAGE_SIZE_OPTIONS: { value: string; label: string }[] = [
  { value: '10', label: '10 items' },
  { value: '25', label: '25 items' },
  { value: '50', label: '50 items' },
  { value: '100', label: '100 items' },
];
const DEFAULT_PAGE_SIZE = [PAGE_SIZE_OPTIONS[1]];

const DEFAULT_COLUMN_ORDER = COLUMN_DEFINITIONS.map((c) => c.id);
const KNOWN_STATIC_IDS = new Set(DEFAULT_COLUMN_ORDER);

/** Returns the enabled columns and full column order encoded in the `cols` param, or null if absent/invalid. */
function parseColsParam(param: string | null): { enabledColumns: string[]; columnOrder: string[] } | null {
  if (!param) return null;
  const ids = param.split(',').filter((id) => KNOWN_STATIC_IDS.has(id) || id.startsWith(REG_FIELD_ID_PREFIX));
  if (!ids.length) return null;
  // Place param IDs first (in their stored order), then append any remaining defaults
  const columnOrder = [...ids, ...DEFAULT_COLUMN_ORDER.filter((id) => !ids.includes(id))];
  return { enabledColumns: ids, columnOrder };
}

const ALL_PROFILES = 'all';
const LOCKED_STATUSES: UserStatus[] = ['registered', 'attended', 'no_shows'];

const AllProfiles = () => {
  const params = useParams() as { listType?: 'registered' | 'attended' | 'no_shows' | 'all' };

  const lockedStatus = useMemo(() => {
    if (params.listType === 'all') return undefined;
    return LOCKED_STATUSES.includes(params.listType as UserStatus) ? params.listType : undefined;
  }, [params.listType]);

  useLoadAllAttendeesList(lockedStatus);
  const dispatch = useAppDispatch();

  useEffect(() => {
    return () => {
      dispatch(resetAllAttendees());
    };
  }, [dispatch]);

  const users = useAppSelector(selectAllAttendeesUsers);
  const loading = useAppSelector(selectAllAttendeesLoading);
  const total = useAppSelector(selectAllAttendeesTotal);
  const page = useAppSelector(selectAllAttendeesPage);
  const pageSize = useAppSelector(selectAllAttendeesPageSize);
  const currentSort = useAppSelector(selectAllAttendeesSort);
  const currentSortDirection = useAppSelector(selectAllAttendeesSortDirection);
  const totalPages = Math.ceil(total / Number(pageSize));
  const registrationFields = useAppSelector(selectWorkingProjectRegistrationQuestions);
  const channel = useAppSelector(selectActiveChannel);
  const userChannel = useAppSelector(selectUserChannel);
  const projectUuid = useAppSelector(selectWorkingProjectUuid);

  const defaultColumnIds = useMemo(
    () => (isWebinarChannel(userChannel?.type) ? DEFAULT_COLUMN_IDS_WEBINAR : DEFAULT_COLUMN_IDS_TOWNHALL),
    [userChannel?.type]
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const [enabledColumns, setEnabledColumns] = useState<string[]>(() => {
    const fromUrl = parseColsParam(searchParams.get('cols'));
    if (fromUrl) return fromUrl.enabledColumns;
    const fromStorage = projectUuid ? parseColsParam(localStorage.getItem(`cols-${projectUuid}`)) : null;
    return fromStorage?.enabledColumns ?? defaultColumnIds;
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const fromUrl = parseColsParam(searchParams.get('cols'));
    if (fromUrl) return fromUrl.columnOrder;
    const fromStorage = projectUuid ? parseColsParam(localStorage.getItem(`cols-${projectUuid}`)) : null;
    return fromStorage?.columnOrder ?? DEFAULT_COLUMN_ORDER;
  });

  // Sync the Redux fields whenever enabledColumns changes (including on first mount from URL/localStorage).
  useEffect(() => {
    dispatch(setAllAttendeesFields(buildAttendeeFields(enabledColumns)));
  }, [enabledColumns, dispatch]);

  // Keep the `cols` query param and localStorage in sync. Clear both when state matches defaults.
  useEffect(() => {
    const orderedEnabled = columnOrder.filter((id) => enabledColumns.includes(id));
    const defaultOrdered = DEFAULT_COLUMN_ORDER.filter((id) => defaultColumnIds.includes(id));
    const isDefault =
      orderedEnabled.length === defaultOrdered.length && orderedEnabled.every((id, i) => id === defaultOrdered[i]);

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (isDefault) {
          next.delete('cols');
        } else {
          next.set('cols', orderedEnabled.join(','));
        }
        return next;
      },
      { replace: true }
    );

    if (projectUuid) {
      if (isDefault) {
        localStorage.removeItem(`cols-${projectUuid}`);
      } else {
        localStorage.setItem(`cols-${projectUuid}`, orderedEnabled.join(','));
      }
    }
  }, [enabledColumns, columnOrder, setSearchParams, projectUuid, defaultColumnIds]);
  const [selectedProfiles, setSelectedProfiles] = useState<ImportedProfile[]>([]);
  const singleUserAddToList = useRef<ImportedProfile[]>([]);
  // const [singleUserAddToList, setSingleUserAddToList] = useState<ImportedProfile[]>([]);
  const addToListPopover = usePopover({ id: 'add-users-to-list' });

  // Clear the per-row single-user buffer whenever the popover closes.
  useEffect(() => {
    if (!addToListPopover.open) singleUserAddToList.current = [];
  }, [addToListPopover.open]);

  const { displayedUsers, allDisplayedSelected, someDisplayedSelected } = useMemo(() => {
    const displayedUsers = (users as AttendeeUserDefault[]) ?? [];
    const selectedEmails = new Set(selectedProfiles.map((sp) => sp[3]));
    const allDisplayedSelected = displayedUsers.length > 0 && displayedUsers.every((u) => selectedEmails.has(u.email));
    const someDisplayedSelected = !allDisplayedSelected && displayedUsers.some((u) => selectedEmails.has(u.email));
    return {
      displayedUsers,
      selectedEmails,
      allDisplayedSelected,
      someDisplayedSelected,
    };
  }, [selectedProfiles, users]);

  const handleSelectAll = useCallback(() => {
    setSelectedProfiles(() => {
      let next: ImportedProfile[] = [];
      if (!allDisplayedSelected) {
        next = displayedUsers.map((u) => ({
          [RegFieldsEnum.first_name]: u[RegFieldsEnum.first_name] || '',
          [RegFieldsEnum.last_name]: u[RegFieldsEnum.last_name] || '',
          [RegFieldsEnum.email]: u.email,
        }));
      }
      return next;
    });
  }, [allDisplayedSelected, displayedUsers]);

  const handleSelectOne = useCallback((profile: ImportedProfile, isOn: boolean) => {
    setSelectedProfiles((prev) => {
      if (!profile['3'] || profile['3'] === '') return prev; //email may be empty
      let next: ImportedProfile[] = [];
      if (isOn) {
        next = [...prev, profile];
      } else {
        next = prev.filter((u) => u['3'] !== profile['3']);
      }
      return next;
    });
  }, []);

  const handleColumnToggle = useCallback(
    (columnId: string, enabled: boolean) => {
      const next = enabled ? [...enabledColumns, columnId] : enabledColumns.filter((id) => id !== columnId);
      setEnabledColumns(next);
      if (enabled && !columnOrder.includes(columnId)) {
        setColumnOrder((prev) => [...prev, columnId]);
      }
    },
    [columnOrder, enabledColumns]
  );

  const handleColumnReorder = useCallback((newOrder: string[]) => {
    setColumnOrder(newOrder);
  }, []);

  const handleSort = useCallback(
    (sortKey: string) => () => {
      const nextDirection = currentSort === sortKey && currentSortDirection === 'asc' ? 'desc' : 'asc';
      dispatch(setAllAttendeesSort({ sort: sortKey, sortDirection: nextDirection }));
    },
    [currentSort, currentSortDirection, dispatch]
  );

  const getSortDirection = useCallback(
    (sortKey: string): SortDirection => {
      if (currentSort !== sortKey) return SortDirection.none;
      return currentSortDirection === 'asc' ? SortDirection.up : SortDirection.down;
    },
    [currentSort, currentSortDirection]
  );

  const visibleColumns = useMemo(() => {
    const regFieldLookup = new Map((registrationFields ?? []).map((f) => [regFieldId(f.registration_question), f]));
    return columnOrder
      .filter((id) => enabledColumns.includes(id))
      .flatMap((id): (typeof COLUMN_DEFINITIONS)[number][] => {
        const staticDef = COLUMN_DEFINITIONS.find((c) => c.id === id);
        if (staticDef) return [staticDef];
        const regField = regFieldLookup.get(id);
        if (regField)
          return [
            {
              id,
              label: regField.name ?? '',
              sortKey: `profile.${regField.registration_question}`,
              columnWidth: '100px',
              isRemovable: true,
              registrationQuestion: regField.registration_question,
            },
          ];
        return [];
      });
  }, [columnOrder, enabledColumns, registrationFields]);

  const setOpenPopover = useCallbackRef(addToListPopover.setOpenPopover);

  const handleAddSingleUserToList = useCallback(
    (user: AttendeeUserDefault) => {
      singleUserAddToList.current = [{ '1': user['1'] || '', '2': user['2'] || '', '3': user.email }];
      setOpenPopover.current();
    },
    [setOpenPopover]
  );

  const handleSaveAddToList = useCallback(
    async (lists: number[]) => {
      if (!channel || !projectUuid) return;
      const usersToAdd = singleUserAddToList.current.length ? singleUserAddToList.current : selectedProfiles;
      const userModifier = selectedProfiles.length > 1 ? 's' : '';
      const listModifier = lists.length > 1 ? 's' : '';

      try {
        await AddUsersToList({ channel, projectUuid, passcodeLists: lists, usersToAdd });

        toastTrigger(`User${userModifier} successfully added to ${lists.length} list${listModifier}`, {
          type: EToastTypes.success,
          autoClose: 5000,
        });
      } catch (e) {
        console.error(e);
        toastTrigger(`There was a problem adding user${userModifier} to list${listModifier}`, {
          type: EToastTypes.error,
          autoClose: 5000,
        });
      } finally {
        if (singleUserAddToList.current.length) {
          singleUserAddToList.current = [];
        } else {
          setSelectedProfiles([]);
        }
      }
    },
    [channel, projectUuid, selectedProfiles]
  );

  const clearSelection = useCallback(() => setSelectedProfiles([]), []);

  const getHeight = useCallback(() => {
    return `calc(100vh - ${selectedProfiles.length ? 290 : 235}px)`;
  }, [selectedProfiles.length]);

  return (
    <Container
      data-component="AllProfiles"
      className="flex fd-column overflow-hidden"
      height="calc(100vh - 136px)"
      minHeight="calc(100vh - 136px)"
    >
      <ProfileListActions lockedStatus={lockedStatus} />

      <AllProfilesBulkActions
        selectedCount={selectedProfiles.length || 0}
        onClearSelection={clearSelection}
        onAddToList={addToListPopover.setOpenPopover}
      />
      <AddUserToList onSave={handleSaveAddToList} />
      <Container minHeight={getHeight()} height={getHeight()}>
        <OptionalComponent display={(!users || !users.length) && !loading}>
          <NoProfiles isAllProfiles={params.listType === ALL_PROFILES} />
        </OptionalComponent>
        <OptionalComponent display={!!users && users.length > 0 && !loading}>
          <Container style={{ overflow: 'auto' }} minHeight={getHeight()} height={getHeight()}>
            <Table>
              <TableHead
                style={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'var(--gray-1000)',
                  zIndex: 2,
                  borderTop: 'none',
                }}
              >
                <TableRow>
                  <th style={{ width: 64, minWidth: 64 }}>
                    <Container className="padding-h-32">
                      <Checkbox
                        value="select-all"
                        checked={allDisplayedSelected}
                        partial={someDisplayedSelected}
                        onChange={handleSelectAll}
                        selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
                        unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
                      />
                    </Container>
                  </th>
                  {visibleColumns.map(({ id, label, sortKey, columnWidth }) => (
                    <th key={id} style={{ width: columnWidth, minWidth: columnWidth }}>
                      <div className="flex ai-center gap-4">
                        <span>{label}</span>
                        {sortKey && (
                          <SortButton onClick={handleSort(sortKey)} sortDirection={getSortDirection(sortKey)} />
                        )}
                      </div>
                    </th>
                  ))}
                  <th />
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedUsers.map((user) => (
                  <ProfileListRow
                    key={`${user.bl_profile}-${user.email}`}
                    user={user}
                    visibleColumns={visibleColumns}
                    selected={selectedProfiles.map((sp) => sp[3]).includes(user.email)}
                    onSelect={handleSelectOne}
                    onAddClick={handleAddSingleUserToList}
                  />
                ))}
              </TableBody>
            </Table>
          </Container>
        </OptionalComponent>
      </Container>

      <Container className="flex ai-center jc-space-between padding-v-16" background="var(--BG)">
        <Container minWidth="0px">
          <Activity mode={totalPages > 1 ? 'visible' : 'hidden'}>
            <Paging page={page} totalPages={totalPages} setPage={(p) => dispatch(setAllAttendeesPage(p))} />
          </Activity>
        </Container>

        <Container width="175px">
          <CustomSelect
            labelHeight="32px"
            hideHeader
            id="profile-list-page-count"
            Placeholder="Show"
            onChange={(options) => dispatch(setAllAttendeesPageSize(options[0]?.value as '10' | '25' | '50' | '100'))}
            defaultOptions={DEFAULT_PAGE_SIZE}
            options={PAGE_SIZE_OPTIONS}
            permanentDropdownIcon
            dynamicMenuPlacement
            isSearchable={false}
            isMultiSelect={false}
            hidePlaceholderIfInputHasValue={false}
            hidePlaceholderOnInputFocus={false}
            RenderUnfocusedLabelContent={({ option }) => {
              return (
                <Container className="flex fd-row fa-center ai-center gap-8" height="32px">
                  <Text design="GlobalIcoText3">Show:</Text>
                  <Text design="GlobalIcoText1">{option.label}</Text>
                </Container>
              );
            }}
            RenderFocusedLabelContent={({ option }) => {
              return (
                <Container className="flex fd-row fa-center ai-center gap-8" height="32px">
                  <Text design="GlobalIcoText3">Show:</Text>
                  <Text design="GlobalIcoText1">{option.label}</Text>
                </Container>
              );
            }}
          />
        </Container>
      </Container>

      <ProfileColumnsSelect
        enabledColumns={enabledColumns}
        columnOrder={columnOrder}
        onToggle={handleColumnToggle}
        onReorder={handleColumnReorder}
      />
    </Container>
  );
};

export default AllProfiles;
